import React from 'react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { Mail } from 'lucide-react';
export function TeamPage() {
  const executiveTeam = [{
    name: 'Dr. Ahmed Khan',
    role: 'President',
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    specialty: 'Consultant Cardiologist, NHS',
    bio: "Dr. Khan has over 20 years of experience in cardiology and has been instrumental in establishing KMA UK's charitable initiatives. He leads our strategic direction and overseas partnerships."
  }, {
    name: 'Dr. Sarah Malik',
    role: 'General Secretary',
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    specialty: 'GP Principal, London',
    bio: 'Dr. Malik manages the day-to-day operations of KMA UK and coordinates our events and member communications. She is passionate about community health and medical education.'
  }, {
    name: 'Dr. Mohammad Ali',
    role: 'Treasurer',
    image: 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    specialty: 'Consultant Neurologist, Birmingham',
    bio: 'Dr. Ali oversees all financial matters and ensures transparency in our charitable work. He brings extensive experience in healthcare management and financial governance.'
  }, {
    name: 'Dr. Fatima Shah',
    role: 'Charity Coordinator',
    image: 'https://images.unsplash.com/photo-1594824476967-48c8b964273f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    specialty: 'Consultant Paediatrician, Manchester',
    bio: 'Dr. Shah leads our humanitarian projects and coordinates medical camps in Pakistan. Her dedication to child health has transformed lives in underserved communities.'
  }];
  const trustees = [{
    name: 'Dr. Imran Hassan',
    role: 'Trustee',
    specialty: 'Consultant Surgeon'
  }, {
    name: 'Dr. Ayesha Rahman',
    role: 'Trustee',
    specialty: 'GP & Medical Educator'
  }, {
    name: 'Dr. Tariq Mahmood',
    role: 'Trustee',
    specialty: 'Consultant Psychiatrist'
  }, {
    name: 'Dr. Zainab Ahmed',
    role: 'Trustee',
    specialty: 'Consultant Radiologist'
  }];
  return <>
      <SEO title="Our Team" description="Meet the trustees and executive committee members leading KMA UK's mission of medical excellence and humanitarian service." data-id="element-761" />

      <div className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-20" data-id="element-762">
        <div className="container mx-auto px-4 text-center" data-id="element-763">
          <FadeIn data-id="element-764">
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-765">
              Our Leadership Team
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto" data-id="element-766">
              Dedicated professionals volunteering their time and expertise to
              serve our community and advance our charitable mission.
            </p>
          </FadeIn>
        </div>
      </div>

      {/* Executive Committee */}
      <Section data-id="element-767">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-768">
          <h2 className="text-3xl font-serif font-bold text-primary-900 mb-4" data-id="element-769">
            Executive Committee
          </h2>
          <p className="text-gray-600" data-id="element-770">
            Our executive team brings together decades of medical experience and
            a shared commitment to excellence and service.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto" data-id="element-771">
          {executiveTeam.map((member, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-772">
              <Card className="h-full border-none shadow-lg hover:shadow-xl transition-shadow" data-id="element-773">
                <div className="aspect-square overflow-hidden" data-id="element-774">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" data-id="element-775" />
                </div>
                <CardContent className="p-6 text-center" data-id="element-776">
                  <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-777">
                    {member.name}
                  </h3>
                  <p className="text-primary-700 font-semibold text-sm mb-2" data-id="element-778">
                    {member.role}
                  </p>
                  <p className="text-gray-500 text-xs mb-3" data-id="element-779">
                    {member.specialty}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed" data-id="element-780">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Board of Trustees */}
      <Section background="gray" data-id="element-781">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-782">
          <h2 className="text-3xl font-serif font-bold text-primary-900 mb-4" data-id="element-783">
            Board of Trustees
          </h2>
          <p className="text-gray-600" data-id="element-784">
            Our trustees provide governance, oversight, and strategic guidance
            to ensure we fulfill our charitable objectives with integrity.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-id="element-785">
          {trustees.map((trustee, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-786">
              <Card className="text-center h-full" data-id="element-787">
                <CardContent className="p-6" data-id="element-788">
                  <div className="h-20 w-20 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-bold mx-auto mb-4" data-id="element-789">
                    {trustee.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-790">
                    {trustee.name}
                  </h3>
                  <p className="text-primary-700 font-medium text-sm mb-2" data-id="element-791">
                    {trustee.role}
                  </p>
                  <p className="text-gray-500 text-xs" data-id="element-792">{trustee.specialty}</p>
                </CardContent>
              </Card>
            </FadeIn>)}
        </div>
      </Section>

      {/* Contact CTA */}
      <Section className="text-center" data-id="element-793">
        <FadeIn data-id="element-794">
          <div className="max-w-2xl mx-auto" data-id="element-795">
            <Mail className="h-10 w-10 mx-auto mb-4 text-primary-600" data-id="element-796" />
            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-797">
              Get in Touch with Our Team
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-798">
              Have questions about our work or interested in collaborating? We'd
              love to hear from you.
            </p>
            <div className="bg-gray-50 rounded-lg p-6 inline-block" data-id="element-799">
              <p className="text-sm text-gray-600 mb-2" data-id="element-800">General Inquiries</p>
              <a href="mailto:info@kmauk.org" className="text-lg font-semibold text-primary-700 hover:underline" data-id="element-801">
                info@kmauk.org
              </a>
            </div>
          </div>
        </FadeIn>
      </Section>
    </>;
}